import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton } from "@/components/Reveal";
import { ISLAND } from "@/data/island";

export default function IslandPage() {
  return (
    <>
      <PageHero
        eyebrow="The Island"
        title={ISLAND.title}
        image={ISLAND.heroImage}
        imgClassName="object-[center_30%]"
      >
        The largest gem in Clew Bay &mdash; 700 acres of beaches, hills and 360-degree views.
      </PageHero>

      <section className="bg-navy-800 py-24 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <Eyebrow>Tour of Collanmore Island</Eyebrow>
            <div className="mt-8 space-y-6">
              {ISLAND.paragraphs.map((p, i) => (
                <FadeIn key={i} delay={i * 0.05}>
                  <p className="text-sm font-light leading-relaxed text-mist sm:text-base">{p}</p>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.2}>
              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <GoldButton href="/the-lodge" testId="island-lodge-cta">Explore The Lodge</GoldButton>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.1}>
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full border border-gold/40" aria-hidden="true" />
              <img
                src={ISLAND.introImage}
                alt="The view from the highest point of Collanmore Island, over the whole of Clew Bay"
                data-testid="island-intro-image"
                className="relative aspect-[4/3] w-full object-cover"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-navy-950 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Eyebrow>The Island in Pictures</Eyebrow>
          <div className="mt-10 grid auto-rows-[180px] grid-cols-2 gap-3 sm:auto-rows-[240px] sm:grid-cols-4">
            {ISLAND.gallery.map((src, i) => (
              <FadeIn key={src} delay={i * 0.07} className={`h-full ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                <figure className="group relative h-full w-full overflow-hidden border border-white/5">
                  <img
                    src={src}
                    alt={`Collanmore Island ${i + 1}`}
                    data-testid={`island-gallery-${i + 1}`}
                    className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  />
                </figure>
              </FadeIn>
            ))}
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2">
            <FadeIn>
              <Link
                to="/the-lodge"
                data-testid="island-to-lodge"
                className="group relative block min-h-[260px] overflow-hidden border border-white/5"
              >
                <img
                  src="/images/lodge-main.jpg"
                  alt="Collanmore Island Lodge"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.26em] text-gold">Sleeps 22</p>
                    <h3 className="mt-2 font-serif text-3xl text-ivory">Collanmore Island Lodge</h3>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
              </Link>
            </FadeIn>
            <FadeIn delay={0.1}>
              <Link
                to="/getting-here"
                data-testid="island-to-getting-here"
                className="group relative block min-h-[260px] overflow-hidden border border-white/5"
              >
                <img
                  src="/images/getting-here-hero.jpg"
                  alt="Getting to Collanmore Island"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.26em] text-gold">Rosmoney Pier</p>
                    <h3 className="mt-2 font-serif text-3xl text-ivory">Getting Here</h3>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
      <section className="bg-navy-900 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="relative overflow-hidden border border-gold/25">
              <video
                src="/videos/arrival.mp4"
                poster="/videos/arrival-poster.jpg"
                preload="metadata"
                autoPlay
                muted
                loop
                playsInline
                data-testid="arrival-video"
                className="aspect-video w-full object-cover"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy-950/85 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Arrive by Air</p>
                <h3 className="mt-2 max-w-2xl font-serif text-3xl text-ivory sm:text-4xl">
                  Coming in to land on Collanmore Island &mdash; why not arrive that way for your special event?
                </h3>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
